import { useCallback, useEffect, useRef, useState } from 'react';

/** Debounced copy of a value, for search boxes that query the server. */
export function useDebounce<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Debounced function; the pending call is cancelled on unmount. */
export function useDebouncedCallback<Args extends unknown[]>(fn: (...args: Args) => void, delayMs = 250): (...args: Args) => void {
  const saved = useRef(fn);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    saved.current = fn;
  });
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return useCallback(
    (...args: Args) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => saved.current(...args), delayMs);
    },
    [delayMs],
  );
}
